import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Broker } from 'shared';

import {
  commitImport,
  deleteImport,
  discardImport,
  purgeAllTrades,
  recomputePositions,
  resetImport,
  uploadImport,
} from '../../lib/api/endpoints';
import { investmentThesisKeys } from '../../lib/investmentQueryKeys';
import { notificationKeys } from '../notifications/queries';
import { notify } from '../notifications/toasts';
import { positionKeys } from '../positions/queries';

import { importKeys } from './queries';

export function useUploadImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { accountId: string; broker: Broker; file: File }) => uploadImport(input),
    onSuccess: (importRecord) => {
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
      queryClient.setQueryData(importKeys.detail(importRecord.id), importRecord);
    },
  });
}

export function useCommitImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => commitImport(id),
    onSuccess: (importRecord) => {
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
      void queryClient.invalidateQueries({ queryKey: positionKeys.all });
      void queryClient.invalidateQueries({ queryKey: investmentThesisKeys.all });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      queryClient.setQueryData(importKeys.detail(importRecord.id), importRecord);
      notify({
        kind: 'IMPORT_DONE',
        variant: 'success',
        title: `Import complete - ${importRecord.fileName}`,
        description: `${importRecord.summary?.tradeRows ?? 0} trades imported`,
      });
    },
  });
}

export function useDiscardImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => discardImport(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
    },
  });
}

export function useResetImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resetImport(id),
    onSuccess: (importRecord) => {
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
      queryClient.setQueryData(importKeys.detail(importRecord.id), importRecord);
    },
  });
}

export function useDeleteImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteImport(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
      void queryClient.invalidateQueries({ queryKey: positionKeys.all });
      void queryClient.invalidateQueries({ queryKey: investmentThesisKeys.all });
    },
  });
}

export function usePurgeAllTrades() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => purgeAllTrades(accountId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
      void queryClient.invalidateQueries({ queryKey: positionKeys.all });
      void queryClient.invalidateQueries({ queryKey: investmentThesisKeys.all });
    },
  });
}

export function useRecomputePositions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => recomputePositions(accountId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: positionKeys.all });
    },
  });
}
